import { clsx } from '@alexis/helpers/clsx';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import React, { useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import { translateNumber } from '@wego/alexis/helpers/translation';

import { getReviewScoreDisplay } from '@helpers/review';

import StarIcon from '@icons/star_rounded.svg';

import { useReviewRatingType } from '@hooks/useReviewRatingType';

import { getLocaleState, getTranslationsState } from '@redux/selectors';

import { HotelViewModel } from '@wegoTypes/hotels/hotelViewModel';
import { HotelSearchResultHotel } from '@wegoTypes/hotels/metasearch/hotelSearchResultHotel';
import { RateViewModel } from '@wegoTypes/hotels/rateViewModel';

import HotelImageCarousel from '../HotelImageCarousel';
import './googleMap.css';
import styles from './HotelCardMap.module.scss';

interface HotelCardMapProps {
  hotelViewModel: HotelViewModel;
  onHotelCardMapClick: () => void;
  onMouseLeave: () => void;
  searchId: string;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
}

const HotelCardMap: React.FC<HotelCardMapProps> = ({
  hotelViewModel,
  onHotelCardMapClick,
  onMouseLeave,
  searchId,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}) => {
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);
  const { getReviewRatingTypeColorClassName, getReviewRatingTypeDisplay } = useReviewRatingType();

  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  const { pathname, search } = useLocation();

  const params = useParams<{ districtId?: string }>();

  // Remove all filter params from search
  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);
    [
      'modal',
      'sort',
      'order',
      'name',
      'deals',
      'amenities',
      'brands',
      'chains',
      'districts',
      'price',
      'accommodation_types',
      'guest_rating',
      'guest_score',
      'stars',
      'open_calendar',
      'selected_hotel_ids',
      'view',
    ].map((item) => searchParams.delete(item));

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search]);

  // Get best deal rate
  const bestDeal = useMemo<RateViewModel | undefined>(() => {
    if (hotelViewModel.filteredRates.length > 0) {
      return hotelViewModel.filteredRates[0];
    }
    return undefined;
  }, [hotelViewModel.filteredRates]);

  const rateHandoffHref = useCallback<(rate: RateViewModel) => string>(
    (rate: RateViewModel) => {
      return appendSearchParams(rate.handoffUrl, {
        wc: wegoAnalyticsClientId,
        ws: wegoAnalyticsClientSessionId,
      });
    },
    [wegoAnalyticsClientId, wegoAnalyticsClientSessionId],
  );

  const hotelDetailHref = useCallback<(hotel: HotelSearchResultHotel) => string>(
    (hotel: HotelSearchResultHotel) => {
      const pathname$ = !!params.districtId
        ? pathname.replace(`/d${params.districtId}`, '')
        : pathname;

      return appendSearchParams(
        `${window.location.origin}${pathname$}/${hotel.id}${searchWithoutExtraParams}`,
        { search_id: searchId },
      );
    },
    [pathname, params.districtId, searchWithoutExtraParams, searchId],
  );

  const handleOnCardClick = () => {
    window.open(
      !!hotelViewModel.sponsoredAd
        ? rateHandoffHref(bestDeal!)
        : hotelDetailHref(hotelViewModel.hotel),
      '_blank',
      'noreferrer',
    );
    onHotelCardMapClick();
  };

  return (
    <div
      className={styles.hotelCardMap}
      onClick={handleOnCardClick}
      onMouseLeave={(e) => {
        e.stopPropagation();
        onMouseLeave();
      }}
    >
      <HotelImageCarousel
        className={styles.hotelImage}
        hotelImages={hotelViewModel.hotel.images}
        onSelectedIndexChanged={setSelectedImageIndex}
        selectedIndex={selectedImageIndex}
      />

      <div className={styles.hotelDetail}>
        <div className={styles.name}>{hotelViewModel.hotel.name}</div>

        <div className={styles.starsAndRating}>
          {!!hotelViewModel.hotel.star ? (
            <div className={styles.star}>
              {hotelViewModel.hotel.star}
              <StarIcon className={styles.icon} />
            </div>
          ) : null}

          {!!hotelViewModel.hotel.reviews[0] ? (
            <>
              <div className={styles.dotSeparator}>&middot;</div>
              <div
                className={clsx(
                  styles.review,
                  getReviewRatingTypeColorClassName(hotelViewModel.hotel.reviews[0].score, styles),
                )}
              >
                {translateNumber(
                  getReviewScoreDisplay(hotelViewModel.hotel.reviews[0].score),
                  locale === 'fa',
                )}
              </div>
              {getReviewRatingTypeDisplay(hotelViewModel.hotel.reviews[0].score, translations)}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default HotelCardMap;
